/**
 * The Billing line under the Edit model dialog's Backing picker, and the
 * billing warnings a move can raise. The gateway prices a model by its own
 * billing entry before any catalog price, whatever upstream serves it, so a
 * stale entry follows the model onto its new backing:
 *   - Display-only (TRA-1383). On 2026-09-29 `cartage/chuck` moved from a
 *     deployment on Cartage's own account (its entry display-only, so
 *     nothing charged) onto our Baseten account, and the entry stayed, so
 *     every token was free until someone deleted it by hand.
 *   - Hourly (TRA-1422). An hourly entry means the GPU invoice pays for the
 *     model, so the nightly token cycle skips its traffic without an alert.
 *     Moved onto a catalog model, the tokens are never billed and nobody is
 *     told.
 * Pure module so the rules are testable without React.
 */

import type { ModelBilling } from '@/api/generated/model'
import type { BackingPatch, UpstreamKind } from '@/components/models/backing'

function money(value: number | null): string {
  return value === null ? 'unset' : `$${value.toFixed(2)}`
}

const DISPLAY_ONLY = ' Display only. Nothing is charged.'

/** One sentence on how the gateway prices this model today. */
export function billingLine(billing: ModelBilling | null): string {
  if (billing === null) return 'Billing is not configured for this team.'
  const suffix = billing.display_only ? DISPLAY_ONLY : ''
  switch (billing.kind) {
    case 'catalog_list_price':
      return `Bills at the catalog list price.${suffix}`
    case 'team_rate':
      return (
        `Team rate: ${money(billing.input_per_m)} in, ${money(billing.cached_per_m)} cached, ` +
        `${money(billing.output_per_m)} out, per 1M tokens.${suffix}`
      )
    case 'hourly':
      return `Billed per deployment hour: ${money(billing.deployment_hour_usd)}/h.${suffix}`
    case 'none':
      return 'No price is set. Usage will show as unpriced.'
  }
}

/** Where a backing sits: its upstream, and the catalog slug when it is a catalog model. */
export interface BackingPlace {
  upstream_id: string | null
  catalog_slug: string | null
}

/** Which billing warning the dialog shows for a move; the box under it drops the entry. */
export type BillingWarning = 'display_only' | 'hourly'

/**
 * True when saving would keep a display-only billing entry on a model whose
 * new backing may run on our account: the model moves to another upstream,
 * or from a custom deployment (which may be the customer's own account)
 * onto a catalog model on the same upstream.
 */
function keepsDisplayOnlyEntry(billing: ModelBilling, current: BackingPlace, next: BackingPlace): boolean {
  if (!billing.display_only) return false
  if (next.upstream_id !== current.upstream_id) return true
  return current.catalog_slug === null && next.catalog_slug !== null
}

/**
 * True when saving would keep an hourly entry on a model whose new backing
 * runs on no GPU of the team's: a catalog model, or a custom deployment on
 * an upstream the gateway does not probe through Baseten. A custom Baseten
 * target keeps the entry, since the GPU billing list covers it.
 */
function keepsHourlyEntry(
  billing: ModelBilling,
  next: BackingPlace,
  upstreams: readonly UpstreamKind[],
): boolean {
  if (billing.kind !== 'hourly') return false
  if (next.catalog_slug !== null) return true
  return upstreams.find((u) => u.id === next.upstream_id)?.healthCheckType !== 'baseten'
}

/**
 * The warning a move raises, or null for none. `next` is null while the
 * pick is incomplete or keeps the backing. The hourly text wins when both
 * rules fire: one box, one drop. The model needs an entry of its own
 * (`entry_key`): a team-wide display-only flag is not an entry to drop.
 */
export function billingWarning(
  billing: ModelBilling | null,
  current: BackingPlace | null,
  next: BackingPlace | null,
  upstreams: readonly UpstreamKind[],
): BillingWarning | null {
  if (billing === null || billing.entry_key === null || current === null || next === null) return null
  if (keepsHourlyEntry(billing, next, upstreams)) return 'hourly'
  return keepsDisplayOnlyEntry(billing, current, next) ? 'display_only' : null
}

/** What the warning box says, and the label of the box that drops the entry. */
export interface BillingWarningCopy {
  message: string
  label: string
}

export function billingWarningCopy(warning: BillingWarning): BillingWarningCopy {
  switch (warning) {
    case 'display_only':
      return {
        message:
          'This model has a display-only billing entry, so nothing is charged. After this ' +
          'change the new backing may run on our account.',
        label: "Also remove the display-only billing entry so usage bills at the new backing's price.",
      }
    case 'hourly':
      return {
        message:
          'This model is billed per GPU hour. The new backing does not run on that GPU, so its ' +
          'usage would not be charged at all.',
        label:
          "Also remove the per-hour price so usage bills at the new backing's price. " +
          'This also removes the fallback prices on that entry.',
      }
  }
}

/**
 * The GPU billing line under the Billing line (TRA-1422). A GPU-billed
 * team pays for GPU time by Baseten model id, and Save adds the id of a
 * custom Baseten target to that list in the same write as the move. Null
 * when there is nothing to say: a team without GPU billing (`gpuModelIds`
 * null), or a save that does not move the model onto a Baseten deployment.
 */
export function gpuBillingLine(gpuModelIds: string[] | null, patch: BackingPatch | null): string | null {
  if (gpuModelIds === null || patch === null || !('custom' in patch)) return null
  const modelId = patch.custom.baseten_model_id
  if (modelId === null || modelId === undefined || modelId === '') return null
  if (gpuModelIds.includes(modelId)) return `Baseten model ${modelId} is already billed.`
  return `Adds Baseten model ${modelId}. The old model stays billed until its deployment is deleted.`
}
